import {
  ActionError,
  Button,
  Field,
  FieldGrid,
  FormBlock,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  Skeleton,
  Textarea,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { TextField } from '@/features/clinic-profile/blocks'
import { placeFieldErrors } from '@/features/clinic-profile/form'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import { errorMessage } from '@/lib/errors'
import { routes } from '@/routes'
import { useCreateIntegration, useIntegrationsCatalog, type IntegrationWithKey } from './api'
import {
  INTEGRATION_KEYS,
  integrationFieldKey,
  integrationInput,
  validateIntegration,
  type IntegrationFormState,
} from './form'
import { IntegrationKey } from './integration-key'
import { PermissionsFields } from './permissions-fields'

/**
 * Novo app de integração (docs/areas/integracoes.md, "Novo app"; RN-INT-01 a 03). Arquétipo: cadastro. Criado, a
 * tela mostra a chave uma única vez, com o `curl` de teste, e leva ao app.
 */

export function NewIntegrationPage() {
  const catalog = useIntegrationsCatalog()
  const create = useCreateIntegration()
  const form = useFormState<IntegrationFormState>(
    () => ({ name: '', description: '', scopes: [] }),
    validateIntegration,
  )
  const formRef = useRef<HTMLFormElement>(null)
  const [created, setCreated] = useState<IntegrationWithKey | null>(null)

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!form.check(INTEGRATION_KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    create.mutate(integrationInput(form.values), {
      onSuccess: setCreated,
      onError: (error) => {
        form.refuse(placeFieldErrors<IntegrationFormState>(error, integrationFieldKey).fields)
        focusFirstInvalid(formRef.current)
      },
    })
  }

  if (created) return <CreatedIntegration created={created} />

  const failure = create.isError
    ? placeFieldErrors<IntegrationFormState>(create.error, integrationFieldKey)
    : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)

  return (
    <form ref={formRef} onSubmit={submit} noValidate>
      <PageHeader
        LinkComponent={Link}
        crumbs={[{ label: 'Integrações', href: routes.admin.integrations }, { label: 'novo app' }]}
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title="Novo app"
            subtitle="O app lê e escreve na clínica pela API e pelo MCP, só no que você permitir. A chave aparece uma vez, ao criar."
          />

          <div className="mt-6 flex flex-col gap-4">
            {showFailure ? (
              <ActionError
                title="Não foi possível criar o app"
                description={
                  failure.unplaced.length > 0
                    ? failure.unplaced.join(' ')
                    : `${errorMessage(create.error)} Nada foi criado; o que você escolheu continua na tela.`
                }
              />
            ) : null}

            <FormBlock
              title="Identificação"
              note="O nome aparece na trilha de ações em tudo que o app fizer, e no e-mail às gestoras quando a chave for regenerada."
            >
              <FieldGrid>
                <TextField
                  form={form}
                  name="name"
                  label="Nome do app"
                  maxLength={60}
                  placeholder="CRM da clínica"
                  hint="Como o sistema que vai usar a chave: o CRM, o n8n, a agenda."
                />
                <Field
                  label="Descrição"
                  optional
                  error={form.errors.description}
                  meta={`${form.values.description.trim().length}/200`}
                  className="col-span-full"
                >
                  <Textarea
                    rows={2}
                    maxLength={200}
                    value={form.values.description}
                    onChange={(event) => form.update({ description: event.target.value })}
                    onBlur={() => form.touch('description')}
                  />
                </Field>
              </FieldGrid>
            </FormBlock>

            <FormBlock
              title="Permissões"
              note="O app faz, em cada recurso, o que a recepção faz, e nada além do que estiver marcado aqui. Nunca administra a clínica, arquiva, exclui nem vê faturamento."
            >
              {catalog.isError ? (
                <LoadError
                  title="Não conseguimos carregar as permissões"
                  error={catalog.error}
                  onRetry={() => void catalog.refetch()}
                />
              ) : catalog.isPending ? (
                <div
                  role="status"
                  aria-label="Carregando as permissões"
                  className="flex flex-col gap-3"
                >
                  <Skeleton className="h-[48px] w-full" />
                  <Skeleton className="h-[48px] w-full" />
                  <Skeleton className="h-[48px] w-full" />
                </div>
              ) : (
                <PermissionsFields
                  catalog={catalog.data}
                  value={form.values.scopes}
                  onChange={(scopes) => form.update({ scopes })}
                  error={form.errors.scopes}
                />
              )}
            </FormBlock>

            <FormFooter
              secondary={
                <Button variant="outline" asChild>
                  <Link to={routes.admin.integrations}>Cancelar</Link>
                </Button>
              }
              primary={
                <Button type="submit" loading={create.isPending} loadingLabel="Criando o app…">
                  Criar app
                </Button>
              }
            />
          </div>
        </ReadingColumn>
      </Page>
    </form>
  )
}

/** Criado: a chave uma única vez, o teste e o caminho para o app. */
function CreatedIntegration({ created }: { created: IntegrationWithKey }) {
  const { integration, key } = created
  return (
    <>
      <PageHeader
        LinkComponent={Link}
        crumbs={[
          { label: 'Integrações', href: routes.admin.integrations },
          { label: integration.name },
        ]}
      />
      <Page>
        <ReadingColumn wide>
          <PageTitle
            title={`App criado: ${integration.name}`}
            subtitle="Entregue a chave a quem vai configurar o sistema. Ela vale até você regenerar ou revogar."
          />
          <div className="mt-6 flex flex-col gap-6">
            <IntegrationKey appKey={key} />
            <div className="flex flex-wrap gap-3">
              <Button asChild>
                <Link to={routes.admin.integration(integration.id)}>Abrir o app</Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to={routes.admin.integrationTab(integration.id, 'webhooks')}>
                  Criar um webhook
                </Link>
              </Button>
            </div>
          </div>
        </ReadingColumn>
      </Page>
    </>
  )
}
